import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ShareDocumentApiPayload } from 'services/documents/DocumentRepository';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import { trackError } from '@proposify/frontend-observability';
import { QueryKeys } from 'utils/queryKeys';

const trackShareDocumentError = (reason: unknown, trackExtra: Record<string, string | string[]>) => {
  trackError(reason, {
    tags: {
      module: 'useShareDocumentMutation',
      shareApi: 'documentService',
    },
    extra: {
      ...trackExtra,
      shareApi: 'documentService',
    },
  });
};

type ShareDocumentMutationVariables = {
  contentId: string;
  fromEmails: string[];
  toEmails: string[];
  shareDocumentPayload: ShareDocumentApiPayload;
};

export const useShareDocumentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ShareDocumentMutationVariables>({
    mutationFn: async ({ contentId, fromEmails, toEmails, shareDocumentPayload: payload }) => {
      const documentServicePayload = {
        id: contentId,
        payload: {
          email: {
            from: fromEmails.map((email) => ({ email })),
            to: toEmails.map((email) => ({ email })),
            subject: payload.subject,
            body: payload.body || '',
            cc: payload.cc_emails?.map((email) => ({ email })) || [],
            bcc: payload.bcc_emails?.map((email) => ({ email })) || [],
          },
        },
      };

      try {
        await apiDocumentServiceRepository.shareDocument(documentServicePayload);
        await queryClient.invalidateQueries({
          queryKey: QueryKeys.getDocumentApprovalStateBase(contentId),
        });
      } catch (error) {
        trackShareDocumentError(error, { contentId, fromEmails, toEmails });
        throw error;
      }
    },
  });
};
